import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type ColumnHelp = { label: string; help: string }

/**
 * A small "?" button that toggles a popover defining each variant-table column.
 * Dismissed by clicking outside or pressing Escape.
 *
 * The popover is rendered in a portal with fixed positioning so it escapes the
 * VariantTable's `overflow-hidden` container (otherwise the bottom rows get
 * clipped when the detail panel shrinks the table area, GEN-3147). A high
 * z-index keeps it above the IGV browser, which sets z-index up to 99999.
 */
const ColumnLegendButton: React.FC<{
  items: ColumnHelp[]
  /** Popover heading. Defaults to the column-guide wording. */
  heading?: string
  /** Accessible label for the toggle and dialog. */
  ariaLabel?: string
  /** Button tooltip text. */
  title?: string
  /** Base data-testid; toggle is `${testId}-toggle`. */
  testId?: string
  /** Width of the label column in the definition grid. */
  labelWidth?: number
  /** Optional small caveat shown under the definitions. */
  footer?: string
}> = ({
  items,
  heading = 'Column guide',
  ariaLabel = 'Column definitions',
  title = 'What do these columns mean?',
  testId = 'column-legend',
  labelWidth = 72,
  footer,
}) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | null>(null)

  // Anchor the fixed popover to the button's current screen position. Horizontally
  // we align the popover's right edge to the button (so right-side triggers stay
  // clear of the viewport edge, GEN-3147) but clamp left into the viewport so
  // left-side triggers (e.g. the TAG legend) don't overflow off-screen (GEN-3224).
  // Vertically we open into whichever side has more room and cap maxHeight to that
  // space (with overflow-y scroll) so the content is never clipped off-screen on
  // short viewports (GEN-3246). 320 == the w-80 popover width below.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    const width = 320
    const margin = 8
    const gap = 4
    const maxLeft = Math.max(window.innerWidth - width - margin, margin)
    const left = Math.min(Math.max(r.right - width, margin), maxLeft)
    const spaceBelow = window.innerHeight - r.bottom - gap - margin
    const spaceAbove = r.top - gap - margin
    if (spaceBelow >= spaceAbove) {
      setPos({ top: r.bottom + gap, left, maxHeight: Math.max(spaceBelow, 80) })
    } else {
      setPos({ bottom: window.innerHeight - r.top + gap, left, maxHeight: Math.max(spaceAbove, 80) })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-expanded={open}
        title={title}
        data-testid={`${testId}-toggle`}
        className="border-border-subtle text-text-subtle hover:text-text-heading hover:border-text-subtle flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none"
      >
        ?
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label={ariaLabel}
            data-testid={testId}
            style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, maxHeight: pos.maxHeight }}
            className="border-border-subtle z-[100000] w-80 overflow-y-auto rounded border bg-white p-3 text-xs normal-case shadow-lg"
          >
            <h3 className="text-text-heading mb-2 text-xs font-semibold">{heading}</h3>
            <dl className="flex flex-col gap-1.5">
              {items.map((it) => (
                <div key={it.label} className="grid gap-2" style={{ gridTemplateColumns: `${labelWidth}px 1fr` }}>
                  <dt className="text-text-heading font-medium">{it.label}</dt>
                  <dd className="text-text-subtle font-normal">{it.help}</dd>
                </div>
              ))}
            </dl>
            {footer && <p className="text-text-subtle mt-2 text-[11px] italic">{footer}</p>}
          </div>,
          document.body
        )}
    </>
  )
}

export default ColumnLegendButton
